/**
 * StaffPortalCalendarService
 * Calendar-feed endpoints for the staff portal. Today: privacy-aware
 * birthdays. Future streams (anniversaries, events) can land here.
 * Route: api/staff-portal/calendar
 */
import { StaffPortalBaseService } from './StaffPortalBaseService';
export class StaffPortalCalendarService extends StaffPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? StaffPortalCalendarService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? StaffPortalCalendarService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Returns birthday rows. <c>month</c> filters to a single month (1..12);
     * <c>todayOnly</c> returns just today's celebrants for dashboard tiles.
     */
    async getBirthdays(month, todayOnly = false) {
        return (await this.getBirthdaysRaw(month, todayOnly)).data ?? [];
    }
    getBirthdaysRaw(month, todayOnly = false) {
        const params = new URLSearchParams();
        if (month != null)
            params.append('month', String(month));
        if (todayOnly)
            params.append('todayOnly', 'true');
        const qs = params.toString();
        return this.get(`birthdays${qs ? `?${qs}` : ''}`);
    }
    /**
     * Returns the list of employees currently on approved leave today.
     * Server-side filter: only `Leave.StatusID == APPROVED` (1) AND
     * `StartDate <= today <= EndDate`. Privacy-safe — same first-name +
     * last-initial convention as `getBirthdays`.
     */
    async getOnLeave() {
        return (await this.getOnLeaveRaw()).data ?? [];
    }
    getOnLeaveRaw() {
        return this.get('on-leave');
    }
    /**
     * Returns approved leaves that haven't started yet, ordered by soonest
     * start. <c>days</c> caps how far ahead to look (default 30, max 90 on
     * the server). On the response rows, <c>daysRemaining</c> represents
     * "days until the leave starts" (model is reused).
     */
    async getUpcomingLeave(days = 30) {
        return (await this.getUpcomingLeaveRaw(days)).data ?? [];
    }
    getUpcomingLeaveRaw(days = 30) {
        return this.get(`upcoming-leave?days=${days}`);
    }
    /**
     * The delegated-duty hand-over for a leave — which duties were handed over and
     * the privacy-safe name of the colleague covering each. Readable by any staff
     * member (not owner-gated) so the team can find who's covering someone on leave.
     */
    async getLeaveCover(leaveId) {
        return (await this.getLeaveCoverRaw(leaveId)).data ?? [];
    }
    getLeaveCoverRaw(leaveId) {
        return this.get(`cover/${leaveId}`);
    }
    /**
     * The current user's subscribe-able ICS calendar feed (approved leave +
     * public holidays). Returns the descriptor including the full tokenised
     * `url` to paste into Apple/Google/Outlook "Subscribe to calendar".
     * Creates a token on first call.
     */
    async getCalendarFeed() {
        return (await this.getCalendarFeedRaw()).data ?? null;
    }
    getCalendarFeedRaw() {
        return this.get('feed-url');
    }
    /** Convenience: just the subscribe-able feed URL (empty string if none). */
    async getCalendarFeedUrl() {
        return (await this.getCalendarFeed())?.url ?? '';
    }
    /**
     * Revoke the current feed URL and mint a new one (use if the URL leaked).
     * Existing calendar subscriptions stop updating; the user re-subscribes
     * with the returned new `url`.
     */
    async rotateCalendarFeed() {
        return (await this.rotateCalendarFeedRaw()).data ?? null;
    }
    rotateCalendarFeedRaw() {
        return this.post('feed-url/rotate');
    }
}
StaffPortalCalendarService.DEFAULT_SERVICE_PATH = 'hr';
StaffPortalCalendarService.BASE_PATH = 'api/staff-portal/calendar';
//# sourceMappingURL=StaffPortalCalendarService.js.map